<template>
	<PageLoading :loading="initLoading">
		<PagePillRow>
			<TagGroup :tags="filters" v-model="user.activeTransactionFilter" />
		</PagePillRow>
		<PagePadding
			class="mt-5 space-y-5"
			:class="{ 'pointer-events-none opacity-50': user.loadingTransactions }"
			v-if="user.transactions.length"
		>
			<div v-for="group in groupedTransactions">
				<SubtitleText>{{ group.date }}</SubtitleText>
				<div class="mt-2 space-y-1.5">
					<ProfileTransaction
						v-for="item in group.items"
						:type="item.type"
						:coins="item.coins"
						:case="item.case"
						:battleInfo="item.battleInfo"
						:date="new Date(item.createdAt)"
						:fair="item.fair"
						:item="item.item"
					/>
				</div>
			</div>
			<div
				class="flex h-10 items-center justify-center"
				v-if="user.page !== user.pages && user.pages"
			>
				<LoaderIcon class="text-separator-secondary h-5 w-5" />
			</div>
		</PagePadding>
		<PageEmpty
			:class="{ 'pointer-events-none opacity-50': user.loadingTransactions }"
			:title="t('empty.title')"
			:desc="t('empty.desc')"
			v-else
		/>
	</PageLoading>
</template>
<script setup lang="ts">
import PagePadding from '@/components/PagePadding.vue'
import SubtitleText from '@/components/SubtitleText.vue'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useUser } from '@/stores/user'
import PageLoading from '@/components/PageLoading.vue'
import TagGroup from '@/components/TagGroup.vue'
import PagePillRow from '@/components/PagePillRow.vue'
import LoaderIcon from '@/assets/icons/loader.svg'
import PageEmpty from '@/components/PageEmpty.vue'
import ProfileTransaction from '@/components/ProfileTransaction.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({
	messages: {
		en: {
			filters: {
				all: 'All',
				games: 'Games',
				deposits: 'Deposits',
				purchases: 'Purchases',
				claims: 'Gift Claims'
			},
			empty: {
				title: 'no transactions found',
				desc: 'New ones will appear here'
			}
		},
		ru: {
			filters: {
				all: 'Всё',
				games: 'Игры',
				deposits: 'Депозиты',
				purchases: 'Покупки',
				claims: 'Выводы гифтов'
			},
			empty: {
				title: 'транзакции не найдены',
				desc: 'Новые появятся здесь'
			}
		}
	}
})

const filters = computed(() => [
	{ text: t('filters.all') },
	{ text: t('filters.games') },
	{ text: t('filters.deposits') },
	{ text: t('filters.purchases') },
	{ text: t('filters.claims') }
])

const user = useUser()

const initLoading = ref(true)

watch(
	() => user.uuid,
	() => {
		if (!user.uuid) {
			return
		}

		user.filterChangedTransactions()
	},
	{ immediate: true }
)

watch(
	() => user.loadingTransactions,
	() => {
		if (user.loadingTransactions) {
			return
		}

		initLoading.value = false
	},
	{ immediate: true }
)

const groupedTransactions = computed(() => {
	const groups: { date: string; items: typeof user.transactions }[] = []

	let lastDate = ''

	user.transactions.forEach((item) => {
		const toFormat = new Date(item.createdAt)

		let date = toFormat.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric'
		})

		if (toFormat.getFullYear() !== new Date().getFullYear()) {
			date = new Date(item.createdAt).toLocaleDateString('en-US', {
				month: 'short',
				day: 'numeric',
				year: 'numeric'
			})
		}
		if (toFormat.toDateString() === new Date().toDateString()) {
			date = 'Today'
		}
		if (
			toFormat.toDateString() === new Date(Date.now() - 86400000).toDateString()
		) {
			date = 'Yesterday'
		}

		if (date !== lastDate) {
			groups.push({
				date,
				items: [item]
			})

			lastDate = date
		} else {
			groups[groups.length - 1].items.push(item)
		}
	})

	return groups
})

const scroll = (e: Event) => {
	const target = e.target as HTMLDivElement

	const scrollTop = target.scrollTop
	const scrollHeight = target.scrollHeight
	const clientHeight = target.clientHeight

	if (scrollHeight - clientHeight - scrollTop < 420) {
		user.debouncedFetchMoreTransactions()
	}
}

onMounted(() => {
	const view = document.querySelector('#view')

	if (!view) {
		return
	}

	view.addEventListener('scroll', scroll)
})

onBeforeUnmount(() => {
	const view = document.querySelector('#view')

	if (!view) {
		return
	}

	view.removeEventListener('scroll', scroll)
})
</script>
